import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import { sanitizeHTML } from '../../utilities';
import Copyright from './Copyright';

const Footer = ({ className, ...props }) => {
  const {
    fontFamily,
    footer,
    copyright,
    window: { borderRadius, boxShadow },
    overlapMenu
  } = useConfig();
  const { chatWindow } = useStores();
  const isEnabledOverlapMenu = !!(overlapMenu && overlapMenu.enabled);

  /**
   * If footer and powered by is disabled
   * return null.
   */
  if (!footer && !copyright) {
    return null;
  }

  /**
   * Render only floating copyright (without Footer).
   * Also, remove the copyright in full screen mode.
   */
  if (copyright && copyright.floatingText) {
    if (!chatWindow.isFullScreen) {
      return (
        <Copyright
          floating
          textFontFamily={fontFamily}
          textColor={copyright.textColor}
          htmlContent={copyright.content}
          enabledOverlapMenu={isEnabledOverlapMenu}
        />
      );
    }

    return null;
  }

  /**
   * Next, we render the footer with content and copyright.
   * Copyright still optional.
   */
  const { height, backgroundColor, content } = footer;

  return (
    <Root
      {...props}
      className={className}
      backgroundColor={backgroundColor}
      defaultHeight={height}
      isFullScreen={chatWindow.isFullScreen}
      textFontFamily={fontFamily}
      borderRadius={borderRadius}
      boxShadow={boxShadow}
      isContent={Boolean(content)}
    >
      {content && <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />}
      {copyright && (
        <Copyright
          textFontFamily={fontFamily}
          textColor={copyright.textColor}
          htmlContent={copyright.content}
          enabledOverlapMenu={isEnabledOverlapMenu}
        />
      )}
    </Root>
  );
};

Footer.propTypes = {
  className: PropTypes.string
};

const Root = styled.footer`
  position: relative;
  top: -1px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: ${({ isContent }) => (isContent ? 'space-between' : 'flex-end')};
  box-sizing: border-box;
  width: 100%;
  min-width: auto;
  height: ${({ defaultHeight }) => `${defaultHeight || 32}px`};
  min-height: auto;
  padding: 10px;
  margin: 0;
  font-family: ${props => props.textFontFamily};
  font-size: 10px;
  font-weight: 400;
  text-align: left;
  border: none;
  border-radius: ${({ isFullScreen, borderRadius }) =>
    isFullScreen ? 0 : `0 0 ${borderRadius}px ${borderRadius}px`};
  background-color: ${props => props.backgroundColor} !important;
  box-shadow: ${props => props.boxShadow};

  &::before,
  &::after {
    content: '';
    display: none;
  }

  div {
    margin: 0;
  }
`;

export default observer(Footer);
